import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Popover } from '@mui/material';
import classNames from 'classnames';

import { CurrencySymbolEnum } from '../../../lib/enums/currency.enum';
import lang from '../../../lib/language';
import {
  CompanyLogoAndNameState,
  SymbolAndOptionalExchangeCode,
} from '../../../lib/models/fundamental/types';
import { RootState } from '../../../lib/store';
import { marketDataSubscribeTradeSubscription, nbboSubscription } from '../../../lib/store/market-data/subscriptionHelpers';
import { hasValidCookie } from '../../../lib/store/selectors';
import { formatCurrencyBySymbol } from '../../../lib/util/DataHelpers';
import { NullableNumber, TypedObject } from '../../../lib/util/types';

import InfoIcon from '../../../assets/img/icon-info-outline.svg';
import { useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import MobileDrawer from '../../../shared_components/MobileDrawer/MobileDrawer';
import ClickableImage from '../../ClickableImage/ClickableImage';

import styles from '../styles.module.scss';

const popoverId = 'market-price-popover';
const MIN_VALID_PRICE = 0;

type Props = {
  symbol: string;
  tradePrice: number | null;
  bidPrice: NullableNumber;
  askPrice: NullableNumber;
  isMarketOpen: boolean;
  isMyAccount?: boolean;
  isSymbolDetailsBigScreen?: boolean;
  isUserRestricted?: boolean;
  currencySymbol?: CurrencySymbolEnum;
}

const MarketPriceInfo = ({
  askPrice,
  bidPrice,
  isMarketOpen,
  isMyAccount,
  isSymbolDetailsBigScreen,
  isUserRestricted,
  tradePrice,
  symbol,
  currencySymbol,
}: Props) => {
  const { isMobile } = useIsMobile();
  const [anchorEl, setAnchorEl] = useState(null);
  const hasCookieSession = useSelector<RootState, boolean>((state: RootState) => hasValidCookie(state));
  const companyLogoAndName = useSelector<RootState, TypedObject<CompanyLogoAndNameState>>(
    (state: RootState) => state.fundamental.companyLogoAndName,
  ) ?? {};
  const { stockExchangeCode } = (companyLogoAndName[symbol] ?? {});

  useEffect(() => {
    if (!isMyAccount || !hasCookieSession) {
      return () => {};
    }

    const mappedSymbols: SymbolAndOptionalExchangeCode[] = [ { symbol, stockExchangeCode } ];
    marketDataSubscribeTradeSubscription(mappedSymbols);
    nbboSubscription(mappedSymbols);

    return () => {
      marketDataSubscribeTradeSubscription(mappedSymbols, true);
      nbboSubscription(mappedSymbols, true);
    };
  }, [isMyAccount, symbol, stockExchangeCode, hasCookieSession]);

  const handlePopoverClick = useCallback((event) => setAnchorEl(event.currentTarget), []);
  const handlePopoverClose = useCallback(() => setAnchorEl(null), []);
  const hasBidAsk = askPrice != null && bidPrice != null && askPrice > MIN_VALID_PRICE && bidPrice > MIN_VALID_PRICE;
  const resolvedCurrencySymbol = currencySymbol ?? CurrencySymbolEnum.USD;

  return (
    <div className={classNames(styles.wrapperMarketPrice, { [styles.marketDisabled]: isUserRestricted })}>
      <div className={styles.wrapperTextAndPopover}>
        {lang.headerMyAccountMyStockMarketPrice()}
        {isMarketOpen && hasBidAsk && !isUserRestricted && (
          <div className={styles.infoPopover}>
            <ClickableImage
              src={InfoIcon}
              onClick={handlePopoverClick}
              ariadescribedby={popoverId}
              variant="contained"
            />
            {!isMobile
              ? (
                <Popover
                  id={popoverId}
                  open={!!anchorEl}
                  anchorEl={anchorEl}
                  onClose={handlePopoverClose}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                  classes={{
                    root: styles.popoverRoot,
                    paper: styles.popoverPaper,
                  }}
                >
                  <div className={styles.marketInfoHeader}>
                    {lang.tradingMarketLastTradePrice(formatCurrencyBySymbol(tradePrice, resolvedCurrencySymbol))}
                  </div>
                  <div className={styles.marketInfoAskPrice}>
                    {lang.webTradingCurrentlyAskPrice(` ${formatCurrencyBySymbol(askPrice, resolvedCurrencySymbol)}`)}
                  </div>
                  <div className={styles.marketInfoBidPrice}>
                    {lang.webTradingCurrentlyBidPrice(` ${formatCurrencyBySymbol(bidPrice, resolvedCurrencySymbol)}`)}
                  </div>
                  <div className={styles.marketInfoFooter}>
                    {isSymbolDetailsBigScreen
                      ? lang.webTradingAskAndBidPricesGeneralInfo()
                      : lang.webTradingDisclaimerPopover()}
                  </div>
                </Popover>
              )
              : (
                <MobileDrawer
                  title={lang.mobileTradingTicketMarketPrice()}
                  isOpen={!!anchorEl}
                  onClose={() => setAnchorEl(null)}
                >
                  <div className={styles.mobileInfoWrapper}>
                    <span>
                      {lang.tradingMarketLastTradePrice(formatCurrencyBySymbol(tradePrice, resolvedCurrencySymbol))}
                    </span>
                    <div>
                      <span>{lang.webTradingCurrentlyAskPrice(` ${formatCurrencyBySymbol(askPrice, resolvedCurrencySymbol)}`)}</span>
                      <span className={styles.mobileMarketInfoBidPrice}>
                        {lang.webTradingCurrentlyBidPrice(` ${formatCurrencyBySymbol(bidPrice, resolvedCurrencySymbol)}`)}
                      </span>
                    </div>
                    <span>
                      {isSymbolDetailsBigScreen
                        ? lang.webTradingAskAndBidPricesGeneralInfo()
                        : lang.webTradingDisclaimerPopover()}
                    </span>
                  </div>
                  <Button fullWidth biggerFontSize onClick={() => setAnchorEl(null)}>{lang.commonClose()}</Button>
                </MobileDrawer>
              )}
          </div>
        )}
      </div>
      <div className={styles.rightAligned}>{formatCurrencyBySymbol(tradePrice, resolvedCurrencySymbol)}</div>
    </div>
  );
};

export default MarketPriceInfo;
